import React, { useEffect, useState } from "react";
import { Badge, Card, CardBody, CardHeader, Col, Row, Spinner, Table } from "reactstrap";
import { Link } from "react-router-dom";
import { Timeline, type TimelineStep, type TimelineStepState } from "components/common/Timeline";
import { DateOnly, DateTime } from "components/common/Date";
import { formatCurrency } from "utils/currency";
import StatusBadge from "components/common/StatusBadge";
import { SourceColumnContent, resolveMappedSourceCell } from "components/common/columns";
import {
  EmployeeAdvanceStatus,
  EmployeeAdvanceValueType,
  EmployeeAdvanceValueTypeLabel,
  employeeAdvanceStatusMap,
} from "features/EmployeeAdvances/constants/employee-advance.constants";
import { getPayments } from "features/Payments/services/PaymentService";
import {
  PaymentSourceType,
  paymentStatusMap,
  paymentDirectionMap,
} from "features/Payments/constants/payment.constants";
import type { Payment } from "features/Payments/types/payment.types";
import { getEmployeeAdvanceSettlements } from "features/EmployeeAdvanceSettlements/services/EmployeeAdvanceSettlementService";
import type { Settlement } from "features/EmployeeAdvanceSettlements/types/employee-advance-settlement.types";
import { EmployeeAdvanceSettlementSourceTypeLabel } from "features/EmployeeAdvanceSettlements/constants/employee-advance-settlement.constants";
import {
  resolveEmployeeAdvanceSettlementSourceCode,
  resolveEmployeeAdvanceSettlementSourceLinkId,
} from "features/EmployeeAdvanceSettlements/utils/employeeAdvanceSettlementSource";
import { useEmployeeAdvanceOverview } from "./EmployeeAdvanceOverviewContext";

function employeeName(record: ReturnType<typeof useEmployeeAdvanceOverview>["advance"]) {
  const src = record?.employee;
  if (!src) return record?.employeeId ? `#${record.employeeId}` : "—";
  const fn = src.firstName ?? src.user?.firstName ?? "";
  const ln = src.lastName ?? src.user?.lastName ?? "";
  const name = [fn, ln].filter(Boolean).join(" ").trim();
  return name || src.code?.trim() || `#${record?.employeeId}`;
}

function PaymentsCard({ advanceId }: { advanceId: number }) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getPayments({
      sourceType: `${PaymentSourceType.EMPLOYEE_ADVANCE},${PaymentSourceType.EMPLOYEE_ADVANCE_REFUND}`,
      sourceId: advanceId,
      limit: 10,
    })
      .then((res) => {
        if (res.success) setPayments(res.data.rows);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [advanceId]);

  if (loading) {
    return (
      <Card>
        <CardBody className="text-center py-3">
          <Spinner size="sm" />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="text-uppercase mb-0">Payments</h6>
          <span className="badge bg-light text-dark">{payments.length}</span>
        </div>
        {payments.length === 0 ? (
          <p className="text-muted small mb-0">No payments recorded yet.</p>
        ) : (
          <div className="vstack gap-2">
            {payments.map((p) => {
              const statusInfo = paymentStatusMap[p.status];
              const directionInfo = paymentDirectionMap[p.direction];
              return (
                <div
                  key={p.id}
                  className="d-flex align-items-center justify-content-between"
                >
                  <div className="d-flex flex-column gap-1">
                    <Link
                      to={`/finance/payments/${p.id}`}
                      className="fw-medium text-body text-decoration-none link"
                    >
                      {p.code || `Payment #${p.id}`}
                    </Link>
                    <span className="text-muted small">
                      {directionInfo?.label ?? "Disbursement"}
                      {p.paymentDate ? (
                        <>
                          {" · "}
                          <DateOnly date={p.paymentDate} />
                        </>
                      ) : null}
                      {p.modeOfPayment?.name ? ` · ${p.modeOfPayment.name}` : ""}
                    </span>
                  </div>
                  <div className="d-flex flex-column align-items-end gap-1">
                    <Badge
                      color={statusInfo?.color ?? "secondary"}
                      className="fs-11"
                    >
                      {statusInfo?.label ?? p.status}
                    </Badge>
                    <span
                      className={`small fw-medium ${directionInfo?.color === "success" ? "text-success" : "text-danger"}`}
                    >
                      {directionInfo?.sign ?? "-"}
                      {formatCurrency(p.amount)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function SettlementsCard({ advanceId }: { advanceId: number }) {
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getEmployeeAdvanceSettlements({ employeeAdvanceId: advanceId, limit: 100 })
      .then((res) => {
        if (res.success && res.data?.rows) {
          setSettlements(res.data.rows as Settlement[]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [advanceId]);

  return (
    <Card className="mt-3">
      <CardHeader>
        <h6 className="card-title mb-0">Settlement Records</h6>
      </CardHeader>
      <CardBody className="p-0">
        {loading ? (
          <div className="text-center py-4">
            <Spinner size="sm" className="me-2" />
            <span className="text-muted">Loading settlements…</span>
          </div>
        ) : settlements.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <i className="ri-file-list-3-line fs-24 d-block mb-2 opacity-50" />
            No settlement records yet.
          </div>
        ) : (
          <div className="table-responsive">
            <Table className="table-borderless table-hover align-middle mb-0">
              <thead className="table-light text-muted">
                <tr>
                  <th className="ps-3">Code</th>
                  <th>Source</th>
                  <th>Amount</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {settlements.map((s) => (
                  <tr key={s.id}>
                    <td className="ps-3 fw-medium">{s.code}</td>
                    <td>
                      <SourceColumnContent
                        value={resolveMappedSourceCell(
                          s.sourceType,
                          resolveEmployeeAdvanceSettlementSourceLinkId(
                            s.sourceType,
                            s.sourceId,
                            s.source,
                          ),
                          {
                            typeLabelMap: EmployeeAdvanceSettlementSourceTypeLabel,
                            source: {
                              code: resolveEmployeeAdvanceSettlementSourceCode(s.sourceType, s.source),
                            },
                          },
                        )}
                      />
                    </td>
                    <td className="fw-semibold">{formatCurrency(s.amount)}</td>
                    <td className="text-muted small">
                      <DateOnly date={s.created_at} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Only DRAFT ("Awaiting Disbursement") is genuinely awaiting the next action — everything else
 * (PROCESSED, SETTLED) is a resting/completed milestone, not ongoing work, and should render as a
 * static "done" dot rather than a pulsing "active" one.
 */
function resolveAdvanceTimelineState(
  status: EmployeeAdvanceStatus,
  isLast: boolean,
): TimelineStepState {
  if (status === EmployeeAdvanceStatus.CANCELLED) return "skipped";
  if (isLast && status === EmployeeAdvanceStatus.DRAFT) return "active";
  return "done";
}

const OverviewTab = () => {
  const { advance } = useEmployeeAdvanceOverview();

  const advanceTimeline: TimelineStep[] = (() => {
    if (!advance) return [];
    const history = advance.statusHistory ?? [];
    if (history.length === 0) {
      const s = advance.status as EmployeeAdvanceStatus;
      const label = employeeAdvanceStatusMap[s]?.label ?? s;
      const state = resolveAdvanceTimelineState(s, true);
      return [
        {
          label,
          timestamp: advance.created_at,
          state,
          color: employeeAdvanceStatusMap[s]?.color,
        },
      ];
    }
    return history.map((entry, i) => {
      const isLast = i === history.length - 1;
      const s = entry.status as EmployeeAdvanceStatus;
      const label = employeeAdvanceStatusMap[s]?.label ?? entry.status;
      const state = resolveAdvanceTimelineState(s, isLast);
      const byLabel = entry.user
        ? [entry.user.firstName, entry.user.lastName].filter(Boolean).join(" ").trim() ||
          `#${entry.by}`
        : entry.by != null
          ? `#${entry.by}`
          : undefined;
      return {
        label,
        timestamp: entry.at,
        by: byLabel,
        actor: entry.message ?? undefined,
        state,
        color: employeeAdvanceStatusMap[s]?.color,
      };
    });
  })();

  return (
    <Row>
      <Col lg={8}>
        <Card>
          <CardBody>
            <div className="text-muted">
              <h6 className="mb-3 text-uppercase">Advance details</h6>
              <div className="table-responsive">
                <Table className="table-borderless mb-0">
                  <tbody>
                    {advance?.code && (
                      <tr>
                        <th className="ps-0" scope="row" style={{ width: "180px" }}>
                          Code
                        </th>
                        <td className="text-muted">{advance.code}</td>
                      </tr>
                    )}
                    <tr>
                      <th className="ps-0" scope="row">
                        Value type
                      </th>
                      <td className="text-muted">
                        {advance?.valueType
                          ? EmployeeAdvanceValueTypeLabel[advance.valueType]
                          : "—"}
                      </td>
                    </tr>
                    {advance?.valueType === EmployeeAdvanceValueType.PERCENTAGE && (
                      <>
                        <tr>
                          <th className="ps-0" scope="row">
                            Percentage
                          </th>
                          <td className="text-muted">
                            {advance.percentage != null ? `${advance.percentage}%` : "—"}
                          </td>
                        </tr>
                        <tr>
                          <th className="ps-0" scope="row">
                            Base amount
                          </th>
                          <td className="text-muted">
                            {advance.baseAmount != null ? formatCurrency(advance.baseAmount) : "—"}
                          </td>
                        </tr>
                      </>
                    )}
                    <tr>
                      <th className="ps-0" scope="row">
                        Resolved amount
                      </th>
                      <td className="text-muted fw-medium">{formatCurrency(advance?.amount)}</td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Amount due
                      </th>
                      <td className="text-muted fw-medium">
                        {formatCurrency(
                          Math.max(0, (advance?.amount ?? 0) - (advance?.settledAmount ?? 0)),
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Employee
                      </th>
                      <td>
                        <Link
                          to={`/employees/${advance?.employeeId}`}
                          className="link fw-medium"
                        >
                          {employeeName(advance)}
                        </Link>
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Purpose
                      </th>
                      <td className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                        {advance?.purpose?.trim() ? advance.purpose : "—"}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              {advance?.remarks?.trim() ? (
                <div className="pt-3 border-top border-top-dashed mt-4">
                  <h6 className="mb-2 text-uppercase">Remarks</h6>
                  <p className="text-muted mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {advance.remarks.trim()}
                  </p>
                </div>
              ) : null}

              <div className="pt-3 border-top border-top-dashed mt-4">
                <Row className="gy-3">
                  {advance?.created_at && (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">Created</p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={advance.created_at} />
                        </h5>
                      </div>
                    </Col>
                  )}
                  {advance?.updated_at && (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">Last updated</p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={advance.updated_at} />
                        </h5>
                      </div>
                    </Col>
                  )}
                  <Col lg={3} sm={6}>
                    <div>
                      <p className="mb-2 text-uppercase fw-medium">Status</p>
                      <h5 className="fs-14 mb-0">
                        <StatusBadge value={advance?.status} map={employeeAdvanceStatusMap} />
                      </h5>
                    </div>
                  </Col>
                  {advance?.deleted_at != null && advance.deleted_at !== "" && (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium text-danger">Deleted</p>
                        <h5 className="fs-14 mb-0 text-danger">
                          <DateTime date={advance.deleted_at} />
                        </h5>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            </div>
          </CardBody>
        </Card>
        {advance?.id ? <SettlementsCard advanceId={advance.id} /> : null}
      </Col>

      <Col lg={4}>
        <Card className="mb-3">
          <CardBody>
            <h6 className="mb-4 text-uppercase text-muted">Timeline</h6>
            <Timeline steps={advanceTimeline} />
          </CardBody>
        </Card>
        {advance?.id ? <PaymentsCard advanceId={advance.id} /> : null}
      </Col>
    </Row>
  );
};

export default OverviewTab;
